import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { SidebarMenu } from 'junto-ui';
import PropTypes from 'prop-types';

import { FirebaseNotificationAPI } from '@services/api';
import {
  USER_TYPE,
  checkBonusJourneyEligibility,
  checkHasLeaseAccess,
  hasPermission,
  ALL_PERMISSIONS
} from '@plataforma/utils';
import { GTMServices } from '@services/gtm';
import { getSessionItem } from '@services/api/src/Domains/Enviroments/EnvironmentsService';
import { getUserProfile } from '@services/auth';

import { getRemoteConfigValue } from '../../config/firebase';

const NavigationMenu = ({
  fixed = false,
  activeId,
  clickMenuCallback,
  ...props
}) => {
  const [menuPropsRemote, setMenuPropsRemote] = useState(null);
  const [menuProps, setMenuProps] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isBonusJourneyElegible, setIsBonusJourneyElegible] = useState(false);
  const [hasLeaseAccess, setHasLeaseAccess] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    const getRemoteMenuProps = async () => {
      const result = JSON.parse(
        await getRemoteConfigValue('platform_menu_items')
      );
      setMenuPropsRemote(result);
    };

    getRemoteMenuProps();
  }, []);

  useEffect(() => {
    const getFirebaseNotifications = async () => {
      await FirebaseNotificationAPI.getNotifications()
        .then(result => {
          setNotifications(result);
        })
        .catch(error => {});
    };

    getFirebaseNotifications();
  }, []);

  useEffect(() => {
    const getUserType = async () => {
      const profile = await getUserProfile();
      setUserProfile(profile);
    };

    getUserType();
  }, []);

  useEffect(() => {
    const checkEligibility = async () => {
      const eligible = await checkBonusJourneyEligibility();
      const bonusJourneyConfig = JSON.parse(
        await getRemoteConfigValue('bonusJourney_config')
      );

      return setIsBonusJourneyElegible(
        eligible && bonusJourneyConfig.campaingActive
      );
    };

    checkEligibility();
  }, []);

  useEffect(() => {
    const result = checkHasLeaseAccess();
    setHasLeaseAccess(result);
  }, []);

  useEffect(() => {
    const getMenuItems = async () => {
      if (!menuPropsRemote || !userProfile) return;
      const { mainButton, menuItems } = menuPropsRemote;

      const filteredMenuItems = menuItems.map(menuItem => {
        if (!menuItem.show || !menuItem.profiles.includes(userProfile)) return;

        const result = checkPermissions(menuItem.id);
        if (!result) return;

        return {
          ...menuItem,
          showBadge: menuItem.newItem
            ? hasNotificationBadge(menuItem.id)
            : false,
          redirect: menuItem.redirectTo
        };
      });

      setMenuProps({
        menuItems: filteredMenuItems.filter(item => item !== undefined),
        mainButton: {
          ...mainButton,
          id: `${mainButton.id}-${userProfile}`,
          redirect: mainButton.redirects[userProfile]
        }
      });
    };

    getMenuItems();
  }, [menuPropsRemote, notifications, isBonusJourneyElegible, hasLeaseAccess]);

  const checkPermissions = menuItem => {
    switch (menuItem) {
      case 'reports':
        return (
          userProfile === USER_TYPE.policyholder &&
          hasPermission(ALL_PERMISSIONS.report)
        );
      case 'escalada-bonus':
        return isBonusJourneyElegible;
      case 'painel-locaticia':
        return hasLeaseAccess;
      default:
        return true;
    }
  };

  const hasNotificationBadge = (id) => {
    if (notifications && notifications.length === 0) return false;
    if (!notifications) return true;
    if (!notifications[id]) return true;
    if (notifications[id] && notifications[id].read === false) return true;

    return false;
  };

  const handleClick = event => {
    event.preventDefault();
    if (!menuProps) return;

    const { target } = event;
    const { id: eventTargetId, type: eventType } = target;
    const { mainButton, menuItems } = menuProps;
    const isButton = eventType === 'button';
    const selectedItem = !isButton
    ? menuItems.find(item => item.id === eventTargetId)
    : mainButton;

    if (selectedItem && selectedItem.gtmTags) {
      GTMServices.dispatchGtm(...selectedItem.gtmTags);
    }
    if (selectedItem && selectedItem.newItem) {
      FirebaseNotificationAPI.putReadNotification(selectedItem.id, true);
    }

    const redirectRef = target.getAttribute('data-redirect');
    const redirect = getRedirectLink(
      eventTargetId,
      userProfile,
      redirectRef
    );

    if (clickMenuCallback) return clickMenuCallback(event, redirect);
    window.location = redirect;
  };

  const getRedirectLink = (
    eventId,
    userProfile,
    redirect
  ) => {
    let suffix = '';

    if (eventId === 'painel-express' || eventId === 'cotacao-emissao-Tomador') {
      suffix =
        userProfile === USER_TYPE.policyholder
          ? `quote/modalities-express/`
          : `modalities-express/`;
    }

    return `${getSessionItem(redirect)}${suffix}`;
  };

  return (
    menuProps && (
      <SidebarMenu
        fixed={fixed}
        showLogo
        activeId={activeId}
        {...props}
        buttonProps={menuProps.mainButton}
        menuList={menuProps.menuItems}
        onCallbackMenu={handleClick}
      />
    )
  );
};

NavigationMenu.defaultProps = {
  fixed: false,
  clickMenuCallback: undefined
};

NavigationMenu.propTypes = {
  fixed: PropTypes.bool,
  activeId: PropTypes.string.isRequired,
  clickMenuCallback: PropTypes.func
};

export default withRouter(NavigationMenu);
